import * as React from "react";
import { cn } from "@/lib/utils";

export type BarTone = "success" | "warning" | "danger" | "brand";
export type BarSize = "sm" | "lg";

const FILL_CLASSES: Record<BarTone, string> = {
  success: "bg-rd-success-500",
  warning: "bg-rd-warning-500",
  danger: "bg-rd-danger-500",
  brand: "bg-brand-500",
};

export interface BarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "color"> {
  value: number;
  tone?: BarTone;
  size?: BarSize;
  ariaLabel?: string;
}

export const Bar = React.forwardRef<HTMLDivElement, BarProps>(
  ({ value, tone = "brand", size = "sm", className, ariaLabel, ...rest }, ref) => {
    const clamped = Math.max(0, Math.min(100, value));
    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
        className={cn(
          "relative overflow-hidden rounded-full bg-bg-sunken",
          size === "sm" ? "h-1" : "h-1.5",
          className
        )}
        {...rest}
      >
        <div
          className={cn(
            "absolute inset-y-0 left-0 w-[var(--bar-w)] rounded-full motion-safe:transition-[width] motion-safe:[transition-duration:280ms]",
            FILL_CLASSES[tone]
          )}
          style={{ "--bar-w": `${clamped}%` } as React.CSSProperties}
        />
      </div>
    );
  }
);
Bar.displayName = "Bar";
